import { PublicClientApplication } from '@azure/msal-browser'
import { getApiClient, JWT_LOCAL_STORAGE_KEY, loadUserFromJwtString } from '@src/data/api/api-client'
import { b2cExtraQueryParameters, msalConfig } from '../authConfig'
import { type JwtToken } from '../data/api/user-api/user-api'
import { UserDtoFromJwt } from '../types/User'

/**
 * MSAL should be instantiated outside of the component tree to prevent it from being re-instantiated on re-renders.
 * For more, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
const msalInstance = new PublicClientApplication(msalConfig)

/**
 * B2C error code sent when the user clicks "Forgot your password?" on the sign-in page.
 * B2C surfaces this as an access_denied error in the URL hash instead of handling it inline,
 * even when the custom policy includes SSPR orchestration steps.
 */
export const B2C_FORGOT_PASSWORD_ERROR_CODE = 'AADB2C90118'

export async function signIn(data: JwtToken): Promise<JwtToken> {
  const apiClient = getApiClient()
  return apiClient.post('/Login/GenerateJwtToken', data).then((response) => {
    return response.data as JwtToken
  })
}

export function saveUserToken(jwtToken: string): void {
  localStorage.setItem(JWT_LOCAL_STORAGE_KEY, jwtToken)
}

export function clearUserToken(): void { 
  localStorage.removeItem(JWT_LOCAL_STORAGE_KEY)
}

export function initializeMsal(): PublicClientApplication {
  return msalInstance
}

/**
 * Returns true when the user cancelled the B2C sign-in flow (clicked "Cancel" or closed the
 * browser). B2C returns access_denied in that case — we exclude the forgot-password code
 * because that also arrives as access_denied but is handled separately by handleB2CForgotPassword().
 */
export function isB2CCancelled(): boolean {
  const b2cError = window.location.search + window.location.hash
  return b2cError.includes('access_denied') && !b2cError.includes(B2C_FORGOT_PASSWORD_ERROR_CODE)
}

/**
 * Detects the B2C forgot-password error and redirects straight to the password reset policy.
 * MSAL is bypassed entirely — calling initialize() after B2C returns an error response leaves
 * MSAL in a broken state where loginRedirect() silently fails.
 *
 * @returns true if a redirect was initiated (caller should not render the app)
 */
export function handleB2CForgotPassword(): boolean {
  const b2cError = window.location.search + window.location.hash
  if (!b2cError.includes(B2C_FORGOT_PASSWORD_ERROR_CODE)) return false

  const clientId = import.meta.env.VITE_REGISTRATION_CLIENT_ID
  const authority = import.meta.env.VITE_B2C_AUTHORITY
  const policy = import.meta.env.VITE_B2C_RESET_POLICY
  const redirectUri = encodeURIComponent(window.location.origin + '/auth-page')
  const nonce = crypto.randomUUID()
  window.location.href = `${authority}${policy}/oauth2/v2.0/authorize?client_id=${clientId}&nonce=${nonce}&redirect_uri=${redirectUri}&scope=openid&response_type=id_token&prompt=login&environment=${b2cExtraQueryParameters.environment}`
  return true
}

export function getUser(): UserDtoFromJwt | null {
  const token = localStorage.getItem(JWT_LOCAL_STORAGE_KEY)
  return token ? loadUserFromJwtString(token) : null
}
